import React, { useState } from 'react'
import { makeStyles } from '@material-ui/core/styles'
import styles from '../../styles'
import { FieldRenderProps } from 'react-final-form'
import classNames from 'classnames'
import 'date-fns'
import { useIntl } from 'react-intl'
import 'moment/locale/fr'
import 'moment/locale/en-gb'
import moment from 'moment'
import { Grid } from '@material-ui/core'
import languages from '../../enums/languagesEnum'
import { getNextDate } from '../../utils/getDays/getNextDate'
import { getToday } from '../../utils/getDays/getToday'
import { KeyboardDatePicker, MuiPickersUtilsProvider } from '@material-ui/pickers'
import MomentUtils from '@date-io/moment'
import { MaterialUiPickersDate } from '@material-ui/pickers/typings/date'

const useStyles = makeStyles(
	(theme) => (
		{
			...styles(theme),
			errorStyle: {
				color: '#B92D00',
				fontSize: 10,
				fontWeight: 500,
				opacity: 0.7,
				lineHeight: 1.33
			},
			errorColor: {
				color: '#B92D00 !important'
			},
			inputRoot: {
				borderRadius: 40,
				border: `0.5px solid ${theme.palette.blue['30']}`,
				backgroundColor: theme.palette.blue['5']
			},
			input: {
				padding: '15px 24px'
			},
			focused: {
				border: `0.5px solid ${theme.palette.blue['30']}`
			},
			button: {
				opacity: 0.5
			},
			dropDownPaper: {
				marginTop: 8,
				borderRadius: 8,
				boxShadow: '0 10px 20px 0 rgba(18, 41, 65, 0.2)',

				'& .MuiPickersToolbar-toolbar': {
					backgroundColor: theme.palette.accessibility.main,

					'& .MuiPickersToolbarText-toolbarTxt, & .MuiPickersToolbarText-toolbarBtnSelected': {
						color: 'white'
					}
				}
			},
			buttonManual: {
				display: 'none'
			},
			materialInput: {
				'&:before': {
					left: 0,
					right: 0,
					bottom: 0,
					content: '""',
					position: 'absolute',
					transition: 'border-bottom-color 200ms cubic-bezier(0.4, 0, 0.2, 1) 0ms',
					borderBottom: '2px solid #d0d4d9',
					pointerEvents: 'none'
				},
				'&:after': {
					left: 0,
					right: 0,
					bottom: 0,
					content: '""',
					position: 'absolute',
					transform: 'scaleX(0)',
					transition: 'transform 200ms cubic-bezier(0.0, 0, 0.2, 1) 0ms',
					borderBottom: '2px solid #00ACA9',
					pointerEvents: 'none'
				}
			}
		}
	)
)

interface AcceptingProps {
	id: string,
	label?: string,
	inputTextStyle?: string,
	disableToolbar?: boolean,
	isGreyBackground?: boolean,
	selectFuture?: boolean,
	manual?: boolean,
	setMinDate?: string
	setMaxDate?: string,
	dateToDisable?: (date: MaterialUiPickersDate) => boolean
}

type FormDatePickerFieldProps =
	FieldRenderProps<string>
	& AcceptingProps

const FormDatePickerField: React.FC<FormDatePickerFieldProps> = (
	{
		input,
		meta: {
			touched,
			error
		},
		inputTextStyle,
		disableToolbar = true,
		isGreyBackground = true,
		selectFuture = true,
		manual = false,
		setMinDate,
		setMaxDate,
		dateToDisable
	}
) => {
	const classes = useStyles()
	const intl = useIntl()
	const [open, setOpen] = useState<boolean>(false)

	const nth = (date: Date) => {
		const day: number = date.getDate()
		if (day > 3 && day < 21) return 'th'
		switch (day % 10) {
			case 1:
				return 'st'
			case 2:
				return 'nd'
			case 3:
				return 'rd'
			default:
				return 'th'
		}
	}

	moment.updateLocale(languages.en, {
		week: {
			dow: intl.locale === languages.fr ? 1 : 0
		}
	});

	return (
		<Grid container>
			<MuiPickersUtilsProvider utils={MomentUtils} locale={intl.locale}>
				<KeyboardDatePicker
					autoOk
					disableToolbar={disableToolbar}
					onClose={() => setOpen(false)}
					fullWidth
					open={open}
					variant="inline"
					minDate={setMinDate && setMinDate.length > 0 ? setMinDate : selectFuture ? getNextDate() : undefined}
					maxDate={setMaxDate && setMaxDate.length > 0 ? setMaxDate : selectFuture ? undefined : getToday()}
					minDateMessage={intl.locale === languages.fr ? 'La date ne doit pas être antérieure à la date minimale' : 'Date should not be before minimal date'}
					maxDateMessage={intl.locale === languages.fr ? 'La date ne doit pas être postérieure à la date maximale' : 'Date should not be after maximal date'}
					format={manual ? 'DD/MM/yyyy' : intl.locale === languages.fr ? 'D MMMM yyyy' : `D[${nth(new Date(input.value))}] MMMM yyyy`}
					invalidDateMessage={intl.locale === languages.fr ? 'Format invalide' : 'Invalid Format'}
					value={input.value && input.value.length > 0 ? input.value : null}
					onChange={(a: any) => {
						if (a) {
							return input.onChange(a.format('yyyy-MM-DD'))
						}

						return input.onChange('')
					}}
					KeyboardButtonProps={{
						classes: {
							root: manual ? classes.buttonManual : classes.button
						}
					}}
					InputProps={{
						classes: {
							root: isGreyBackground ? classes.inputRoot : classes.materialInput,
							input: classNames(
								inputTextStyle,
								{
									[classes.input]: isGreyBackground
								}
							),
							focused: classes.focused
						},
						disableUnderline: true,
						readOnly: !manual,
						onClick: () => !manual && setOpen(true)
					}}
					PopoverProps={{
						classes: {
							paper: classes.dropDownPaper
						}
					}}
					FormHelperTextProps={{
						className: inputTextStyle
					}}
					shouldDisableDate={dateToDisable}
				/>
			</MuiPickersUtilsProvider>
			{
				touched &&
				((error && <span className={classes.errorStyle}>{error}</span>))
			}
		</Grid>
	)
}

export default FormDatePickerField
